import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './PackagePurchaseCard.styles';
import {
  getPackagePriceDisplayState,
  type PurchasePackageViewModel,
} from '@/features/Profile/sections/Packages/PackagesSection.helpers';
import { CheckIcon, DiscountPercentIcon } from '@/components/UI/Icons/Icons';
import { formatNumber } from '@/utils/helpers';

type PackagePurchaseCardProps = {
  item: PurchasePackageViewModel;
  onConnect: (item: PurchasePackageViewModel) => void;
};

export function PackagePurchaseCard({
  item,
  onConnect,
}: PackagePurchaseCardProps) {
  const { t } = useTranslation();

  const { hasCombinedLabels, hasOriginalPrice, hasPromoPriceLabel } =
    getPackagePriceDisplayState(item);
  const {
    name,
    showPromotionLabel,
    benefitPercent,
    originalPrice,
    price,
    periodDays,
    walletAmount,
    paidAdsCount,
    discount,
    isHighlighted,
  } = item;

  return (
    <S.Card $packageName={name}>
      <S.CardHeader>
        <S.NameBox>
          <S.Name>{name}</S.Name>
          {hasCombinedLabels && (
            <S.CompactBenefitBadge data-testid="compact-benefit-badge">
              <DiscountPercentIcon aria-hidden="true" />
            </S.CompactBenefitBadge>
          )}
        </S.NameBox>
        {hasPromoPriceLabel && !showPromotionLabel && (
          <S.BenefitBadge>
            {t('pages.cabinet.benefit_percent', {
              discount: benefitPercent,
            })}
          </S.BenefitBadge>
        )}
        {showPromotionLabel && (
          <S.AdditionalDiscountBadge>
            {t('pages.cabinet.additional_discount')}
          </S.AdditionalDiscountBadge>
        )}
      </S.CardHeader>
      <S.PriceBox>
        {hasOriginalPrice && (
          <S.OriginalPrice>{formatNumber(originalPrice, 0)} ₼</S.OriginalPrice>
        )}
        <S.PriceRow>
          <S.Price>{formatNumber(price, 0)} ₼</S.Price>
          <S.Period>
            {t('pages.cabinet.for_package_period', { days: periodDays })}
          </S.Period>
        </S.PriceRow>
      </S.PriceBox>
      <S.Features>
        {walletAmount !== null && (
          <S.Feature>
            <CheckIcon />
            {t('pages.cabinet.package_wallet_amount', {
              amount: formatNumber(walletAmount, 0),
            })}
          </S.Feature>
        )}
        {paidAdsCount !== null && (
          <S.Feature>
            <CheckIcon />
            {t('pages.cabinet.package_paid_ads_count', {
              adsCount: formatNumber(paidAdsCount, 0),
            })}
          </S.Feature>
        )}
        {Boolean(discount) && (
          <S.Feature>
            <CheckIcon />
            {t('pages.cabinet.discount_for_services', { discount })}
          </S.Feature>
        )}
      </S.Features>
      <S.ConnectButton
        data-stat={`package-block-${name}`}
        appearance={isHighlighted ? 'secondary' : 'secondaryWhite'}
        isFullWidth
        borderRadius={14}
        onClick={() => onConnect(item)}
      >
        {t('pages.cabinet.plug_in_package', { name })}
      </S.ConnectButton>
    </S.Card>
  );
}
